import { useState } from "react";
import { Link } from "react-router-dom";
import { downloadNitTemplate } from "@/api/nits";
import { Button } from "@/components/ui/Button";
import { showToast } from "@/components/ui/Toast";
import { PageHeader } from "@/components/ui/PageHeader";

const fileToBase64 = (file: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(",")[1];
      resolve(base64);
    };
    reader.onerror = (error) => reject(error);
  });
};

export const NitImportarPage = () => {
  const [file, setFile] = useState<File | null>(null);
  const [result, setResult] = useState<{ count: number; msg: string } | null>(
    null,
  );
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMsg, setStatusMsg] = useState("");

  const onDownload = async () => {
    try {
      await downloadNitTemplate();
      showToast("success", "Plantilla descargada");
    } catch (err) {
      showToast("error", "Error al descargar", (err as Error).message);
    }
  };

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!file) {
      showToast("warning", "Selecciona un archivo Excel");
      return;
    }
    setLoading(true);
    setProgress(0);
    setStatusMsg("Codificando archivo...");
    setResult(null);

    try {
      const base64 = await fileToBase64(file);
      const clientId = Date.now().toString();
      const protocol = window.location.protocol === "https:" ? "wss:" : "ws:";
      const socket = new WebSocket(
        `${protocol}//${window.location.host}/ws/nits/import/${clientId}`
      );

      socket.onopen = () => {
        setStatusMsg("Enviando archivo al servidor...");
        socket.send(
          JSON.stringify({ fileContent: base64, fileName: file.name })
        );
      };

      socket.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          if (data.status === "processing") {
            setProgress(data.progress || 0);
            setStatusMsg(data.msg || "Procesando...");
          } else if (data.status === "success") {
            setResult({ count: data.count, msg: data.msg });
            showToast("success", data.msg);
            setFile(null);
            setLoading(false);
            const input = document.getElementById("file") as HTMLInputElement | null;
            if (input) input.value = "";
          } else if (data.status === "error") {
            showToast("error", "No se pudo importar", data.msg);
            setLoading(false);
          }
        } catch {
          // ignore malformed data
        }
      };

      socket.onerror = () => {
        showToast("error", "Error de conexión con el servidor");
        setLoading(false);
      };

      socket.onclose = () => {
        setLoading(false);
      };
    } catch (err) {
      showToast("error", "Error al preparar archivo", (err as Error).message);
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-2xl space-y-4">
      <PageHeader
        title="Importar NITs"
        actions={
          <>
            <Button variant="outline" onClick={onDownload}>
              Descargar plantilla
            </Button>
            <Link to="/nits">
              <Button variant="secondary">Volver al listado</Button>
            </Link>
          </>
        }
      />

      <div className="rounded-md border border-accent-blue/30 bg-accent-blue/5 p-3 text-xs text-ink-secondary">
        <strong>Instrucciones:</strong> descarga la plantilla, complétala con
        los datos y súbela. Las columnas requeridas son{" "}
        <code className="rounded bg-slate-100 px-1">NIT</code> y{" "}
        <code className="rounded bg-slate-100 px-1">RAZON_SOCIAL</code>. Los
        NITs existentes conservan sus datos; solo se completan los campos
        vacíos.
      </div>

      {loading ? (
        <div className="space-y-3 rounded-lg bg-white p-6 shadow-card border border-slate-150">
          <div className="flex justify-between text-xs font-semibold">
            <span className="text-ink-secondary">{statusMsg}</span>
            <span className="text-accent-blue">{progress}%</span>
          </div>
          <div className="h-2 w-full rounded-full bg-slate-100 overflow-hidden">
            <div
              className="h-full bg-accent-blue transition-all duration-300 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      ) : (
        <form
          onSubmit={onSubmit}
          className="space-y-4 rounded-lg bg-white p-6 shadow-card"
        >
          <div>
            <label
              htmlFor="file"
              className="block text-sm font-medium text-ink-secondary"
            >
              Archivo Excel (.xlsx, .xls)
            </label>
            <input
              id="file"
              type="file"
              accept=".xlsx,.xls"
              onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              className="mt-2 block w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm file:mr-3 file:rounded file:border-0 file:bg-accent-blue/10 file:px-3 file:py-1 file:text-accent-blue"
              required
            />
            {file ? (
              <p className="mt-2 text-xs text-ink-secondary">
                Archivo: {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </p>
            ) : null}
          </div>

          <div className="flex justify-end">
            <Button type="submit" loading={loading}>
              Importar
            </Button>
          </div>
        </form>
      )}

      {result ? (
        <div className="rounded-md border border-accent-green/30 bg-accent-green/5 p-3 text-sm text-accent-green">
          <strong>Importación exitosa:</strong> {result.count} NIT(s)
          procesado(s). {result.msg}
        </div>
      ) : null}
    </div>
  );
};
